import { useState } from "react";
import { Copy, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	type ChecklistTemplate,
	updateChecklistTemplate,
	deleteChecklistTemplate,
	createChecklistTemplate,
} from "@/services/task-checklist.service";
import { TemplateEditor } from "./TemplateEditor";

export function TemplateCard({
	projectId,
	template,
	onChanged,
}: {
	projectId: string;
	template: ChecklistTemplate;
	onChanged: () => void | Promise<unknown>;
}) {
	const isDefault = template.project_id === null;
	const [editing, setEditing] = useState(false);
	const [busy, setBusy] = useState(false);

	async function handleDelete() {
		if (!window.confirm(`Delete template "${template.name}"?`)) return;
		setBusy(true);
		try {
			await deleteChecklistTemplate(projectId, template.id);
			toast.success("Template deleted");
			await onChanged();
		} catch (e) {
			toast.error("Delete failed", { description: (e as Error).message });
		} finally {
			setBusy(false);
		}
	}

	async function handleDuplicate() {
		setBusy(true);
		try {
			await createChecklistTemplate(
				projectId,
				`${template.name} (copy)`,
				template.items.map((it) => ({ title: it.title })),
			);
			toast.success("Duplicated to project");
			await onChanged();
		} catch (e) {
			toast.error("Duplicate failed", {
				description: (e as Error).message,
			});
		} finally {
			setBusy(false);
		}
	}

	if (editing) {
		return (
			<div className="rounded-lg border border-border bg-surface p-4">
				<TemplateEditor
					initialName={template.name}
					initialItems={template.items.map((it) => it.title)}
					submitLabel="Save changes"
					onCancel={() => setEditing(false)}
					onSubmit={async (name, items) => {
						await updateChecklistTemplate(
							projectId,
							template.id,
							name,
							items,
						);
						toast.success("Template updated");
						setEditing(false);
						await onChanged();
					}}
				/>
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-border bg-surface p-4">
			<div className="flex items-start justify-between gap-2 mb-2">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<h3 className="text-sm font-semibold text-foreground truncate">
							{template.name}
						</h3>
						{isDefault && (
							<span className="text-[10px] font-medium uppercase tracking-wide text-muted bg-muted-subtle rounded px-1.5 py-0.5">
								Default
							</span>
						)}
					</div>
					<p className="text-xs text-muted mt-0.5">
						{template.items.length} item
						{template.items.length === 1 ? "" : "s"}
					</p>
				</div>

				<div className="flex items-center gap-1.5 shrink-0">
					{isDefault ? (
						<Button
							variant="outline"
							size="sm"
							onClick={handleDuplicate}
							disabled={busy}
						>
							{busy ? (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							) : (
								<Copy className="h-3.5 w-3.5 mr-1.5" />
							)}
							Duplicate
						</Button>
					) : (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setEditing(true)}
								disabled={busy}
							>
								<Pencil className="h-3.5 w-3.5 mr-1.5" />
								Edit
							</Button>
							<Button
								variant="destructive_outline"
								size="sm"
								onClick={handleDelete}
								disabled={busy}
								aria-label="Delete template"
							>
								{busy ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<Trash2 className="h-3.5 w-3.5" />
								)}
							</Button>
						</>
					)}
				</div>
			</div>

			<ul className="space-y-1 mt-2">
				{template.items.map((it, i) => (
					<li
						key={i}
						className="flex items-center gap-2 text-xs text-foreground"
					>
						<span className="h-1.5 w-1.5 rounded-full bg-border-strong shrink-0" />
						<span className="truncate">{it.title}</span>
					</li>
				))}
			</ul>
		</div>
	);
}
